import {
  Alert,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { BottomSection } from '../../components/FrontendFormPage';
import { FrontendLayout } from '../../components/FrontendLayout';
import { FormPaneWrapper } from '../../components/form/FormPaneWrapper';
import { getLatestRadiothon, Radiothon } from '../../lib/rails_api/getLatestRadiothon';
import { requestPledgesByEmail } from '../../lib/rails_api/requestPledgesByEmail';

type PropsType = {
  latestRadiothon: Radiothon;
};

type LocatePledgeFormValues = {
  email?: string;
};

const locatePledgeDefaultFormValues: LocatePledgeFormValues = {};

function PayYourPledgePage({ latestRadiothon }: PropsType): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasSuccess, setSuccess] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LocatePledgeFormValues>({ defaultValues: locatePledgeDefaultFormValues });

  async function onSubmit(formValues: LocatePledgeFormValues): Promise<void> {
    if (!formValues.email) {
      return;
    }
    const result = await requestPledgesByEmail(formValues.email);
    if (result.success) {
      setSuccess(true);
      setErrorMessage(null);
    } else {
      setSuccess(false);
      setErrorMessage(result.message);
    }
  }

  return (
    <FrontendLayout>
      <Box bg="white" pb="4">
        <Container maxW="container.md" py="8">
          <Heading as="h1" mb="4" size="4xl" textAlign="center">
            Pay Your {latestRadiothon.name} Pledge
          </Heading>

          <Box py="6" fontSize="larger" lineHeight="tall" textAlign="center">
            <Stack spacing="4">
              <Text>
                Enter the email address for your pledge(s). We&apos;ll automatically look up your
                information and email you a secure link to the check-out page with your details
                filled out — easy!
              </Text>
            </Stack>
          </Box>
        </Container>
      </Box>

      <BottomSection>
        <Container maxW="container.md" pt="8">
          <FormPaneWrapper>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing="4">
                {hasSuccess && (
                  <Alert status="success">
                    <AlertIcon />
                    <Box mr="8">
                      An email has been sent to you with your pledge details and link(s) to complete
                      check-out
                    </Box>
                    <CloseButton
                      position="absolute"
                      right="6px"
                      top="6px"
                      onClick={() => setSuccess(false)}
                    />
                  </Alert>
                )}
                {errorMessage && (
                  <Alert status="error">
                    <AlertIcon />
                    <Box mr="8">{errorMessage}</Box>
                    <CloseButton
                      position="absolute"
                      right="6px"
                      top="6px"
                      onClick={() => setErrorMessage(null)}
                    />
                  </Alert>
                )}
                <FormControl isInvalid={errors.email !== undefined}>
                  <FormLabel>
                    Your email address
                    <Input
                      variant="filled"
                      mt="2"
                      {...register('email', { required: true })}
                      type="text"
                    />
                  </FormLabel>
                  <FormErrorMessage>
                    {errors.email && <span>This field is required</span>}
                  </FormErrorMessage>
                </FormControl>

                <Box>
                  <Button type="submit" variant="solid" colorScheme="pink">
                    Look Up
                  </Button>
                </Box>
              </Stack>
            </form>
          </FormPaneWrapper>
        </Container>
      </BottomSection>
    </FrontendLayout>
  );
}

export const getServerSideProps = async (): Promise<{ props: PropsType }> => {
  const latestRadiothon = await getLatestRadiothon();
  return { props: { latestRadiothon } };
};

export default PayYourPledgePage;
